import { useCalendarPreferences } from '../../../../../platform/util/calendarPreferences';
import React, { useState } from 'react';
import { Box, Link, Typography, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert } from '@mui/material';
import { getBookingShareStatus, hasBookableShareSlot } from './bookingShareStatus';
import BookingShareNotice from './bookingShareNotice';
import { useApolloClient } from '@apollo/client';
import { formatInTimeZone } from 'date-fns-tz';
import { GET_STAFF_WORKING_HOUR, GET_BOOKING_BY_BOOKING_TYPE_ID } from '../../../../../platform/graphql/queries';
import { BookingTypeStatus } from '../../../../../platform/util/utils';
import ContentCopy from '@mui/icons-material/ContentCopy';
import OpenInNew from '@mui/icons-material/OpenInNew';
import Check from '@mui/icons-material/Check';

const ActionsContainer = ({ url, isDisabled, showLink = false, showLiveButton = true, bookingType, availabilityOptions, onEditSchedule }) => {
    const [isCopied, setIsCopied] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [copyError, setCopyError] = useState('');
    const [checkedStatus, setCheckedStatus] = useState(null);
    const [checking, setChecking] = useState(false);
    const { displayCalendar } = useCalendarPreferences();
    const client = useApolloClient();
    const scheduleStatus = getBookingShareStatus(bookingType, { ...availabilityOptions, displayCalendar });
    const status = checkedStatus || scheduleStatus;

    async function checkBeforeCopy() {
        setCopyError('');
        setCheckedStatus(null);
        if (scheduleStatus) { setConfirmOpen(true); return; }
        if (!bookingType?.id) { handleCopyLink(); return; }
        setChecking(true);
        try {
            const timezone = availabilityOptions?.timezone || 'UTC';
            const now = new Date();
            const today = formatInTimeZone(now, timezone, 'yyyy-MM-dd');
            const [year, month] = today.split('-').map(Number);
            const nextMonth = `${month === 12 ? year + 1 : year}-${String(month === 12 ? 1 : month + 1).padStart(2, '0')}-01`;
            const staffIds = (bookingType.assignedStaffs || []).map(staff => String(staff.staffId));
            const results = await Promise.all([
                ...[today, nextMonth].map(startDate => client.query({ query: GET_STAFF_WORKING_HOUR, variables: { staffIds, bookingTypeId: String(bookingType.id), startDate, viewType: 'month', timezone }, fetchPolicy: 'network-only' })),
                client.query({ query: GET_BOOKING_BY_BOOKING_TYPE_ID, variables: { bookingTypeId: Number(bookingType.id) }, fetchPolicy: 'network-only' }),
            ]);
            const days = results.slice(0, 2).flatMap(result => result.data?.getStaffAvailability?.availability || []);
            if (!hasBookableShareSlot(days, results[2].data?.getBookingByBookingTypeId, bookingType, now, BookingTypeStatus.DELETED)) {
                setCheckedStatus({ severity: 'warning', title: 'No upcoming bookable times found', message: 'No remaining times were found for this month or next month. Review your date range and availability before sharing. Later dates may still be available.', action: 'Review availability', target: 'hours' });
                setConfirmOpen(true);
            } else handleCopyLink();
        } catch {
            setCheckedStatus({ severity: 'warning', title: 'Availability could not be checked', message: 'Please review the booking page before sharing, or try again later.', action: 'Review availability', target: 'hours' });
            setConfirmOpen(true);
        } finally { setChecking(false); }
    }

    const handleCopyLink = () => {
        navigator.clipboard
            .writeText(url)
            .then(() => {
                setConfirmOpen(false);
                setCopyError('');
                setIsCopied(true);
                setTimeout(() => setIsCopied(false), 3000); // Reset copied state after 3 seconds
            })
            .catch(() => setCopyError('Could not copy the link. Please try again.'));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%' }}>
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Before you share</DialogTitle>
                <DialogContent><BookingShareNotice status={status} />{copyError && <Alert severity="error">{copyError}</Alert>}</DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleCopyLink}>{status?.severity === 'warning' ? 'Copy link anyway' : 'Copy link'}</Button>
                    {onEditSchedule && <Button variant="contained" onClick={() => { setConfirmOpen(false); onEditSchedule(status?.target); }}>{status?.action}</Button>}
                </DialogActions>
            </Dialog>
            {!confirmOpen && copyError && <Alert severity="error">{copyError}</Alert>}
            {/* Display the URL on the left */}
            {showLink && (
                <Typography
                    variant="body1"
                    sx={{
                        maxWidth: '100%',
                        overflow: 'visible',
                        whiteSpace: 'normal',      // allow wrapping
                        wordBreak: 'break-word',   // break long segments
                        overflowWrap: 'anywhere',  // as-needed breaking for very long URLs
                    }}
                >
                    <Link
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            textDecoration: 'none',
                            color: '#003366',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                        }}
                    >
                        {url}
                    </Link>
                </Typography>

            )}

            {/* Action Buttons for Copy and View on the right */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginLeft: 'auto' }}>
                {/* Copy Link Button */}
                <Tooltip title="Copy link" arrow>
                    <IconButton aria-label={checking ? 'Checking availability' : 'Copy link'} disabled={checking} onClick={checkBeforeCopy} color="primary">
                        <ContentCopy fontSize="small" />
                    </IconButton>
                </Tooltip>
                {isCopied && (
                    <Typography variant="caption" color="success.main" sx={{ marginTop: 0.5 }}>
                        <Check fontSize="small" sx={{ verticalAlign: 'middle' }} /> Copied
                    </Typography>
                )}

                {/* Open in New Tab Button */}
                {showLiveButton && (
                    <Tooltip title="Open link in new tab" arrow>
                        <IconButton
                            href={url}
                            target="_blank"
                            rel="noopener"
                            size="small"
                            color="primary"
                        >
                            <OpenInNew fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}

            </Box>
        </Box>
    );
};

export default ActionsContainer;
